
import React, { MouseEventHandler, useEffect, useRef, useState } from "react";
import { useReferenceDataContext } from "../context/ReferenceDataContext";
import ViewTermsContentComp from "./ViewTermsContentComp";

interface TermsAndConditionCheckboxCompProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
}

const TermsAndConditionCheckboxComp: React.FC<TermsAndConditionCheckboxCompProps> = ({
    checked,
    onChange
   
}) => {
    const { supplierTerms } = useReferenceDataContext();

    const [showModal, setShowModal] = useState(false);
    const [canAgree, setCanAgree] = useState(false);
    const bodyScrollRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (showModal) setCanAgree(false);
    }, [showModal]);

    const openModal = () => {
        if (!supplierTerms) return;
        setShowModal(true);
    };

    const closeModal = () => setShowModal(false);

    // (1) Checkbox click behaviour:
    // - when unchecked: opens modal (same as container)
    // - when checked: unchecks immediately without opening modal
    const handleCheckboxClick: MouseEventHandler<HTMLInputElement> = (e) => {
        if (checked) {
            onChange(false);
            return;
        }
        e.preventDefault();
        openModal();
    };

    // (2) Container click behaviour (excluding checkbox and terms link):
    // - when checkbox is unchecked: opens modal
    // - when checkbox is checked: does nothing (to avoid accidentally opening modal after already agreeing)
    const handleContainerClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
        // prevent double trigger when the checkbox itself is clicked
        if ((e.target as HTMLElement).closest("input[type='checkbox']")) return;

        if (checked && !(e.target as HTMLElement).closest('[data-terms-link="true"]')) {
            return;
        }

        openModal();
    };

    // (3) Terms link click behaviour: always opens modal, regardless of checkbox state. Does not trigger container click.
    const handleTermsClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
        e.preventDefault();
        e.stopPropagation();
        openModal();
    };

    const handleAgree = () => {
        onChange(true);     
        closeModal();      
    };

    const onBodyScroll = () => {
        const el = bodyScrollRef.current;
        if (!el) return;

        const thresholdPx = 4;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - thresholdPx;

        if (atBottom) 
            setCanAgree(true);
    };

    return (
        <>
            
            <div
                className="d-flex align-items-start gap-2"
                onClick={handleContainerClick}
                style={{ cursor: checked ? "default" : "pointer" }}>
                <div className="form-check m-0">
                    <input
                        id="termsCheck"
                        className="form-check-input"
                        type="checkbox"
                        checked={checked}
                        onClick={handleCheckboxClick}
                        readOnly
                    />
                </div>
                <div style={{ userSelect: "none" }}>
                    I agree to the{" "}
                            <a
                                href="#"
                                data-terms-link="true"
                                className="text-muted text-decoration-underline"
                                onClick={handleTermsClick}
                                role="button"
                                tabIndex={0}>
                                Terms &amp; Conditions
                            </a>


                </div>
            </div>

            {/* Modal */}
            {supplierTerms && (
                <ViewTermsContentComp
                    show={showModal}
                    termsContent={supplierTerms}
                    onClose={closeModal}
                    bodyScrollRef={bodyScrollRef}
                    onBodyScroll={onBodyScroll}
                    canAgree={canAgree}
                    onAgree={handleAgree}
                />
            )}
        </>
    );
};

export default TermsAndConditionCheckboxComp;
